import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import type { JourneyPhaseCode, TechnicianJourney } from '../../types/technicianJourney';
import { mergeTechnicianPhases } from '../../services/journeyApi';

export type JourneyPhaseNodeData = {
  label: string;
  labelAr: string;
  completed: boolean;
  active: boolean;
  subtitle?: string;
  variant: 'default' | 'success' | 'warning' | 'danger' | 'muted';
  branch?: boolean;
  /** Receives wire from identity hub */
  ingressTop?: boolean;
};

export type JourneyPillarNodeData = {
  label: string;
  labelAr: string;
  completed: boolean;
  detail?: string;
};

export type JourneyHubNodeData = {
  title: string;
  titleAr: string;
  lines: string[];
};

export type JourneyMetricNodeData = {
  label: string;
  labelAr: string;
  lines: string[];
  accent: 'sky' | 'violet' | 'amber' | 'emerald' | 'rose' | 'slate';
  /** Last node in horizontal customer chain — omit right source handle */
  chainEnd?: boolean;
};

const SPINE_Y = 108;
const SPINE_X0 = 32;
const SPINE_GAP = 152;
const PILLAR_Y = 268;
const PILLAR_GAP = 138;
const PILLAR_X0 = 400;

const METRIC_Y = -168;
const METRIC_GAP_X = 138;
const METRIC_X0 = 36;
const HUB_X = 320;
const HUB_Y = -52;

const phaseActive = (journey: TechnicianJourney, code: JourneyPhaseCode): boolean =>
  String(journey.currentPhase).toUpperCase() === code;

function spineX(index: number): number {
  return SPINE_X0 + index * SPINE_GAP;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return s;
  }
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

export function buildTechnicianJourneyFlow(journey: TechnicianJourney): { nodes: Node[]; edges: Edge[] } {
  const p = mergeTechnicianPhases(journey.phases);
  const suspended = String(journey.currentPhase).toUpperCase() === 'SUSPENDED';

  const signupDone = p.signup.completed;
  const profileDone = p.profileCompletion.completed;
  const approvalDone = p.adminApproval.completed;
  const onboardingDone = p.onboarding.completed;
  const portfolioDone = p.portfolio.created;
  const completeDone = String(journey.currentPhase).toUpperCase() === 'COMPLETE' && !suspended;

  const a = journey.activity;
  const q = journey.quotes;
  const perf = journey.performance;
  const qual = journey.quality;
  const eng = journey.engagement;
  const plat = journey.platform;

  const hubLines: string[] = [
    journey.phoneNumber || '—',
    journey.email || '—',
    `City: ${journey.city ?? '—'}`,
    `Registered: ${fmtDate(journey.registrationDate)}`,
    `Terms: ${fmtDate(journey.acceptedTermsAt)}`,
  ];

  const hubNode: Node<JourneyHubNodeData> = {
    id: 'hub-identity',
    type: 'journeyHub',
    position: { x: HUB_X, y: HUB_Y },
    data: {
      title: 'Identity & registration',
      titleAr: 'الهوية والتسجيل',
      lines: hubLines,
    },
  };

  const metricNodes: Node<JourneyMetricNodeData>[] = [
    {
      id: 'metric-activity',
      type: 'journeyMetric',
      position: { x: METRIC_X0, y: METRIC_Y },
      data: {
        label: 'Activity',
        labelAr: 'النشاط',
        accent: 'sky',
        lines: a
          ? [
              `Logins (30d): ${a.loginCountLast30Days}`,
              `Days since activity: ${a.daysSinceLastActivity ?? '—'}`,
              `Last login: ${fmtDate(a.lastLoginDate)}`,
            ]
          : ['No activity payload'],
      },
    },
    {
      id: 'metric-quotes',
      type: 'journeyMetric',
      position: { x: METRIC_X0 + METRIC_GAP_X, y: METRIC_Y },
      data: {
        label: 'Quotes',
        labelAr: 'العروض',
        accent: 'violet',
        lines: q
          ? [
              `Total: ${q.totalQuotes}`,
              `7d / 30d: ${q.quotesLast7Days} / ${q.quotesLast30Days}`,
              `Frequency: ${q.quoteFrequency ?? '—'}`,
              `Last: ${fmtDate(q.lastQuoteDate)}`,
            ]
          : ['No quotes payload'],
      },
    },
    {
      id: 'metric-performance',
      type: 'journeyMetric',
      position: { x: METRIC_X0 + METRIC_GAP_X * 2, y: METRIC_Y },
      data: {
        label: 'Performance',
        labelAr: 'الأداء',
        accent: 'amber',
        lines: perf
          ? [
              `Won / done / live: ${perf.wonProjects} / ${perf.completedProjects} / ${perf.inProgressProjects}`,
              `Cancelled: ${perf.cancelledProjects}`,
              `Win rate: ${fmtPct(perf.winRate)}`,
              `Completion: ${fmtPct(perf.completionRate)}`,
            ]
          : ['No performance payload'],
      },
    },
    {
      id: 'metric-quality',
      type: 'journeyMetric',
      position: { x: METRIC_X0 + METRIC_GAP_X * 3, y: METRIC_Y },
      data: {
        label: 'Quality',
        labelAr: 'الجودة',
        accent: 'emerald',
        lines: qual
          ? [
              `Rating: ${qual.averageRating ?? '—'} (${qual.totalReviews} reviews)`,
              `Complaints: ${qual.complaintsCount}`,
              `Support tickets: ${qual.supportTicketsCount}`,
            ]
          : ['No quality payload'],
      },
    },
    {
      id: 'metric-engagement',
      type: 'journeyMetric',
      position: { x: METRIC_X0 + METRIC_GAP_X * 4, y: METRIC_Y },
      data: {
        label: 'Engagement',
        labelAr: 'التفاعل',
        accent: 'rose',
        lines: eng
          ? [
              `Conversations: ${eng.conversationsCount}`,
              `Response rate: ${eng.responseRate ?? '—'}`,
              `Avg response (min): ${eng.avgResponseTimeMinutes ?? '—'}`,
            ]
          : ['No engagement payload'],
      },
    },
    {
      id: 'metric-platform',
      type: 'journeyMetric',
      position: { x: METRIC_X0 + METRIC_GAP_X * 5, y: METRIC_Y },
      data: {
        label: 'Platform',
        labelAr: 'المنصة',
        accent: 'slate',
        lines: plat
          ? [
              `Profile views: ${plat.profileViews}`,
              `Views (30d): ${plat.profileViewsLast30Days}`,
              `Unique viewers: ${plat.uniqueViewers}`,
            ]
          : ['No platform payload'],
      },
    },
  ];

  const nodes: Node[] = [
    hubNode,
    ...metricNodes,
    {
      id: 'spine-signup',
      type: 'journeyPhase',
      position: { x: spineX(0), y: SPINE_Y },
      data: {
        label: 'Signup',
        labelAr: 'التسجيل',
        completed: signupDone,
        active: phaseActive(journey, 'SIGNUP'),
        subtitle: p.signup.otpVerified ? 'OTP ✓' : 'OTP pending',
        variant: signupDone ? 'success' : phaseActive(journey, 'SIGNUP') ? 'warning' : 'muted',
        ingressTop: true,
      },
    },
    {
      id: 'spine-profile',
      type: 'journeyPhase',
      position: { x: spineX(1), y: SPINE_Y },
      data: {
        label: 'Profile',
        labelAr: 'الملف',
        completed: profileDone,
        active: phaseActive(journey, 'PROFILE'),
        subtitle: p.profileCompletion.profileComplete ? 'Complete' : 'Incomplete',
        variant: profileDone ? 'success' : phaseActive(journey, 'PROFILE') ? 'warning' : 'muted',
      },
    },
    {
      id: 'spine-approval',
      type: 'journeyPhase',
      position: { x: spineX(2), y: SPINE_Y },
      data: {
        label: 'Admin approval',
        labelAr: 'موافقة الإدارة',
        completed: approvalDone,
        active: phaseActive(journey, 'APPROVAL'),
        subtitle:
          p.adminApproval.pendingDataRequests > 0
            ? `${p.adminApproval.pendingDataRequests} pending request(s)`
            : 'No pending',
        variant: approvalDone ? 'success' : phaseActive(journey, 'APPROVAL') ? 'warning' : 'muted',
      },
    },
    {
      id: 'spine-onboarding',
      type: 'journeyPhase',
      position: { x: spineX(3), y: SPINE_Y },
      data: {
        label: 'Onboarding',
        labelAr: 'الإعداد',
        completed: onboardingDone,
        active: phaseActive(journey, 'ONBOARDING'),
        subtitle: `${journey.overallProgress}% overall`,
        variant: onboardingDone ? 'success' : phaseActive(journey, 'ONBOARDING') ? 'warning' : 'muted',
        branch: true,
      },
    },
    {
      id: 'spine-portfolio',
      type: 'journeyPhase',
      position: { x: spineX(4), y: SPINE_Y },
      data: {
        label: 'Portfolio',
        labelAr: 'معرض الأعمال',
        completed: portfolioDone,
        active: phaseActive(journey, 'PORTFOLIO'),
        subtitle: p.portfolio.pastProjectsCount ? `${p.portfolio.pastProjectsCount} projects` : 'None yet',
        variant: portfolioDone ? 'success' : phaseActive(journey, 'PORTFOLIO') ? 'warning' : 'muted',
      },
    },
    {
      id: 'spine-terminal',
      type: 'journeyPhase',
      position: { x: spineX(5), y: SPINE_Y },
      data: suspended
        ? {
            label: 'Suspended',
            labelAr: 'موقوف',
            completed: false,
            active: true,
            subtitle: journey.status,
            variant: 'danger',
          }
        : {
            label: 'Complete',
            labelAr: 'مكتمل',
            completed: completeDone,
            active: phaseActive(journey, 'COMPLETE'),
            subtitle: completeDone ? 'Fully set up' : 'Not yet',
            variant: completeDone ? 'success' : phaseActive(journey, 'COMPLETE') ? 'warning' : 'muted',
          },
    },
  ];

  const ob = p.onboarding;
  const pillars: Node<JourneyPillarNodeData>[] = [
    {
      id: 'pillar-services',
      type: 'journeyPillar',
      position: { x: PILLAR_X0, y: PILLAR_Y },
      data: {
        label: 'Services',
        labelAr: 'الخدمات',
        completed: ob.services.added,
        detail: ob.services.count ? `${ob.services.count} service(s)` : '—',
      },
    },
    {
      id: 'pillar-availability',
      type: 'journeyPillar',
      position: { x: PILLAR_X0 + PILLAR_GAP, y: PILLAR_Y },
      data: {
        label: 'Availability',
        labelAr: 'التوفر',
        completed: ob.availability.set,
        detail: ob.availability.type || '—',
      },
    },
    {
      id: 'pillar-subscription',
      type: 'journeyPillar',
      position: { x: PILLAR_X0 + PILLAR_GAP * 2, y: PILLAR_Y },
      data: {
        label: 'Subscription',
        labelAr: 'الاشتراك',
        completed: ob.subscription.active,
        detail: ob.subscription.categoryNameEn || ob.subscription.categoryNameAr || '—',
      },
    },
    {
      id: 'pillar-smalltasks',
      type: 'journeyPillar',
      position: { x: PILLAR_X0 + PILLAR_GAP * 3, y: PILLAR_Y },
      data: {
        label: 'Small tasks',
        labelAr: 'المهام الصغيرة',
        completed: ob.smallTaskTypes.subscribed,
        detail: ob.smallTaskTypes.count ? `${ob.smallTaskTypes.count} type(s)` : '—',
      },
    },
  ];

  nodes.push(...pillars);

  const edge = (id: string, source: string, target: string, lit: boolean, opts?: Partial<Edge>): Edge => ({
    id,
    source,
    target,
    animated: lit,
    style: {
      stroke: lit ? '#22c55e' : 'var(--border-medium, #94a3b8)',
      strokeWidth: lit ? 2.5 : 1.5,
      ...opts?.style,
    },
    markerEnd: { type: MarkerType.ArrowClosed, color: lit ? '#22c55e' : 'var(--border-medium, #94a3b8)' },
    ...opts,
  });

  const metricStroke = '#6366f1';
  const edges: Edge[] = [];

  metricNodes.forEach((m, i) => {
    edges.push({
      id: `e-hub-m${i}`,
      source: 'hub-identity',
      target: m.id,
      sourceHandle: 'metrics',
      targetHandle: 'ing',
      animated: true,
      style: { stroke: metricStroke, strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: metricStroke },
    });
  });

  edges.push({
    id: 'e-hub-signup',
    source: 'hub-identity',
    target: 'spine-signup',
    sourceHandle: 'pipeline',
    targetHandle: 'from-hub',
    animated: true,
    style: { stroke: '#0ea5e9', strokeWidth: 2.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#0ea5e9' },
  });

  const spineHandle = { sourceHandle: 'out', targetHandle: 'in' };
  edges.push(
    edge('e-s1', 'spine-signup', 'spine-profile', signupDone, spineHandle),
    edge('e-s2', 'spine-profile', 'spine-approval', profileDone, spineHandle),
    edge('e-s3', 'spine-approval', 'spine-onboarding', approvalDone, spineHandle),
    edge('e-s4', 'spine-onboarding', 'spine-portfolio', onboardingDone, spineHandle),
    edge('e-s5', 'spine-portfolio', 'spine-terminal', portfolioDone || completeDone, spineHandle)
  );

  const pillarLit = (done: boolean) => onboardingDone || done;
  edges.push(
    {
      id: 'e-p1',
      source: 'spine-onboarding',
      target: 'pillar-services',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      animated: pillarLit(ob.services.added),
      style: {
        stroke: pillarLit(ob.services.added) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: pillarLit(ob.services.added) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
      },
    },
    {
      id: 'e-p2',
      source: 'spine-onboarding',
      target: 'pillar-availability',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      animated: pillarLit(ob.availability.set),
      style: {
        stroke: pillarLit(ob.availability.set) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: pillarLit(ob.availability.set) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
      },
    },
    {
      id: 'e-p3',
      source: 'spine-onboarding',
      target: 'pillar-subscription',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      animated: pillarLit(ob.subscription.active),
      style: {
        stroke: pillarLit(ob.subscription.active) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: pillarLit(ob.subscription.active) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
      },
    },
    {
      id: 'e-p4',
      source: 'spine-onboarding',
      target: 'pillar-smalltasks',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      animated: pillarLit(ob.smallTaskTypes.subscribed),
      style: {
        stroke: pillarLit(ob.smallTaskTypes.subscribed) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: pillarLit(ob.smallTaskTypes.subscribed) ? '#0ea5e9' : 'var(--border-medium, #94a3b8)',
      },
    }
  );

  return { nodes, edges };
}

/** @deprecated use buildTechnicianJourneyFlow */
export const buildJourneyFlow = buildTechnicianJourneyFlow;
